import {
  ClinicalImpressionAssessmentValueCode,
  ClinicalImpressionCode,
  ClinicalImpressionLocationCode,
  ClinicalImpressionPlanValueCode,
} from '@quromedical/fhir-common';
import type { DeepPartial } from '@quromedical/utils';
import { z } from 'zod';
import { AdmissionSchema } from '../admission/schema';
import {
  BaseUUIDSearchSchema,
  ReferenceSchema,
  UUIDSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import { PaginatedBundleSchema } from '../domain/schema';
import { MediaDataSchema } from '../media/base';
import { MediaGetResponseSchema } from '../media/schema';
import {
  LatestResultMeasuresSchema,
  LatestResultNotesMeasuresSchema,
} from '../vitals/schema';

const noteRefinement = (obj: NoteRefinement, ctx: z.RefinementCtx) => {
  const { planCode, plan, assessmentCode, assessment } = obj;
  if (
    planCode !== ClinicalImpressionPlanValueCode.Continue &&
    plan === undefined
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Plan comments are required for the selected plan.',
      path: ['plan'],
    });
  }
  if (
    assessmentCode !== ClinicalImpressionAssessmentValueCode.Stable &&
    assessment === undefined
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Assessment comments are required.',
      path: ['assessment'],
    });
    return;
  }
};

export const ClinicalImpressionAssessmentValueCodeSchema = z.nativeEnum(
  ClinicalImpressionAssessmentValueCode,
);
export const ClinicalImpressionPlanValueCodeSchema = z.nativeEnum(
  ClinicalImpressionPlanValueCode,
);

export const CreateSOAPNoteSchema = z
  .object({
    assessmentCode: ClinicalImpressionAssessmentValueCodeSchema,
    planCode: ClinicalImpressionPlanValueCodeSchema,
    subjective: z.string().describe('Subjective note-text'),
    objective: z.string().optional().describe('Objective note-text'),
    assessment: z.string().optional(),
    plan: z.string().optional(),
  })
  .superRefine(noteRefinement);

const NoteRefinementSchema = z.object({
  assessmentCode: ClinicalImpressionAssessmentValueCodeSchema,
  planCode: ClinicalImpressionPlanValueCodeSchema,
  subjective: z.string(),
  objective: z.string().optional(),
  assessment: z.string().optional(),
  plan: z.string().optional(),
  dailySummary: z.string().optional(),
  overallSummary: z.string().optional(),
});

export const SOAPNoteSchema = NoteRefinementSchema.partial().extend({
  subjective: z.string().array().optional(),
});

export const StructuredContentTypeSchema = z.literal('nested-comment-nodes');

export const StructuredNoteBaseSchema = z.object({
  id: z.string(),
  display: z.string(),
  selected: z.boolean().optional(),
  color: z
    .string()
    .optional()
    .describe(
      'This represents a color name and not necessarily a hex or some other value',
    ),
  type: z.enum(['comment', 'group']).optional(),
});

export interface StructuredNoteItem extends StructuredNoteBase {
  type: 'comment';
  text?: string;
}

export type StructuredNoteNode = StructuredNoteGroup | StructuredNoteItem;

export interface StructuredNoteGroup extends StructuredNoteBase {
  type: 'group';
  children: Array<StructuredNoteNode>;
}

export const StructuredNoteItemSchema = StructuredNoteBaseSchema.extend({
  type: z.literal('comment'),
  text: z.string().optional(),
});

export const StructuredNoteGroupSchema: z.ZodType<StructuredNoteGroup> =
  StructuredNoteBaseSchema.extend({
    type: z.literal('group'),
    children: z.lazy(() => z.array(StructuredNoteNodeSchema)),
  });
export const StructuredNoteNodeSchema = StructuredNoteGroupSchema.or(
  StructuredNoteItemSchema,
);

export const StructuredContentSchema = z.object({
  /**
   * In the event we choose to modify the data stored in the structuredContent then we can tweak
   * this accordingly, it should make parsing the data easier down the line
   */
  type: StructuredContentTypeSchema,
  data: z.array(StructuredNoteNodeSchema),
});

export interface PartialStructuredContent {
  type: StructuredContentType;
  data?: DeepPartial<StructuredNoteNode>[];
}

const DisplayReferenceSchema = z.object({
  reference: ReferenceSchema.optional(),
  display: z.string().optional(),
});

const clinicalImpressionCodeSchema = z
  .nativeEnum(ClinicalImpressionCode)
  .describe('Code for the type of a clinical impression');

const clinicalImpressionLocationCodeSchema = z
  .nativeEnum(ClinicalImpressionLocationCode)
  .describe('Where clinical impression was taken');

export const CreateNoteSchema = z.object({
  subject: UUIDSchema.describe('Patient ID'),
  code: clinicalImpressionCodeSchema,
  locationCode: clinicalImpressionLocationCodeSchema,
  note: CreateSOAPNoteSchema.describe('The SOAP note'),
  vitals: LatestResultMeasuresSchema,
  media: z
    .array(MediaDataSchema)
    .describe('Media associated with the SOAP note'),
  practitionersToNotify: z
    .array(DisplayReferenceSchema)
    .describe('Practitioners to be notified of the SOAP note'),
  structuredContent: StructuredContentSchema.optional(),
});

export const NoteUpdateSchema = z.object({
  id: UUIDSchema.describe('NoteID to be updated'),
  subjective: z.string().optional().describe('Additional subjective note'),
  media: z.array(MediaDataSchema).describe('Additional media'),
  pinned: z.boolean().optional().describe('Whether the note is pinned'),
});

export const NoteAdmissionSchema = AdmissionSchema.partial().extend({
  id: UUIDSchema,
});

/**
 * Created such that most data can be undefined. This helps to code defensively against changes to
 * the FHIR schema as well as allow for old data to be migrated where most of the below entries will
 * be undefined
 */
export const NoteSchema = z.object({
  id: UUIDSchema,
  code: clinicalImpressionCodeSchema.optional(),
  locationCode: clinicalImpressionLocationCodeSchema.optional(),
  assessor: DisplayReferenceSchema.optional(),
  media: MediaGetResponseSchema.array(),
  note: SOAPNoteSchema,
  vitals: LatestResultNotesMeasuresSchema,
  practitionersToNotify: DisplayReferenceSchema.array(),
  admission: NoteAdmissionSchema.optional(),
  dateCreated: z.string().optional(),
  pinned: z.boolean(),
  structuredContent: StructuredContentSchema.optional(),
});

export const NoteBundleSchema = PaginatedBundleSchema.extend({
  pins: NoteSchema.array(),
  notes: NoteSchema.array(),
});

export const NoteSearchSchema = PaginationSchema.extend({
  search: z.string().optional().describe('Search string'),
  dateFrom: z.string().optional().describe('FHIR Datetime to search from'),
  dateTo: z.string().optional().describe('FHIR Datetime to search until'),
  subject: BaseUUIDSearchSchema.optional().describe('The subject of the note'),
  code: z.string().optional().describe('The code of the note'),
  notCode: z
    .string()
    .optional()
    .describe('The code of the note that should not be included in the search'),
});

type NoteRefinement = z.infer<typeof NoteRefinementSchema>;
export type CreateSOAPNote = z.infer<typeof CreateSOAPNoteSchema>;
export type SOAPNote = z.infer<typeof SOAPNoteSchema>;
export type StructuredContentType = z.infer<typeof StructuredContentTypeSchema>;
export type StructuredNoteBase = z.infer<typeof StructuredNoteBaseSchema>;
export type StructuredContent = z.infer<typeof StructuredContentSchema>;
export type CreateNote = z.infer<typeof CreateNoteSchema>;
export type NoteUpdate = z.infer<typeof NoteUpdateSchema>;
export type Note = z.infer<typeof NoteSchema>;
export type NoteBundle = z.infer<typeof NoteBundleSchema>;
export type NoteSearch = z.infer<typeof NoteSearchSchema>;
/**
 * @deprecated Please use NoteSearch instead
 */
export type QueryParams = NoteSearch;
